import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Observable, from, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';

const PDF_MAGIC = [0x25, 0x50, 0x44, 0x46] as const; // %PDF

async function blobStartsWithPdfMagic(blob: Blob): Promise<boolean> {
  if (!blob || blob.size < PDF_MAGIC.length) {
    return false;
  }
  try {
    const buffer = await new Response(blob).arrayBuffer();
    const bytes = new Uint8Array(buffer).subarray(0, PDF_MAGIC.length);
    return PDF_MAGIC.every((value, index) => bytes[index] === value);
  } catch {
    return false;
  }
}

export function asPdfBlob(blob: Blob): Blob {
  return new Blob([blob], { type: 'application/pdf' });
}

async function readBlobErrorMessage(
  blob: Blob,
  fallback: string,
): Promise<string> {
  try {
    const text = await new Response(blob).text();
    const parsed = JSON.parse(text) as {
      message?: string | string[];
      error?: string;
    };
    if (parsed.message) {
      return Array.isArray(parsed.message)
        ? parsed.message.join(', ')
        : parsed.message;
    }
    if (typeof parsed.error === 'string' && parsed.error.trim()) {
      return parsed.error.trim();
    }
  } catch {
    /* keep fallback */
  }
  return fallback;
}

async function resolveDownloadedDocumentBlob(
  blob: Blob,
  contentTypeHeader: string | null,
  fallbackMessage: string,
): Promise<Blob> {
  if (await blobStartsWithPdfMagic(blob)) {
    return asPdfBlob(blob);
  }

  const type = (blob.type || contentTypeHeader || '')
    .toLowerCase()
    .split(';')[0]
    .trim();

  if (type.includes('json') || type.includes('text') || !type) {
    throw new Error(await readBlobErrorMessage(blob, fallbackMessage));
  }

  return asPdfBlob(blob);
}

async function recoverPdfFromHttpErrorBlob(
  errorBlob: Blob,
  fallbackMessage: string,
): Promise<Blob> {
  if (await blobStartsWithPdfMagic(errorBlob)) {
    return asPdfBlob(errorBlob);
  }
  throw new Error(await readBlobErrorMessage(errorBlob, fallbackMessage));
}

export function mapPdfDownloadHttpError(
  err: unknown,
  fallbackMessage: string,
): Observable<never> {
  if (err instanceof Error && !(err instanceof HttpErrorResponse)) {
    return throwError(() => err);
  }

  const httpErr = err as HttpErrorResponse;
  const errorBlob =
    httpErr?.error instanceof Blob ? (httpErr.error as Blob) : null;

  if (errorBlob && errorBlob.size > 0) {
    return from(recoverPdfFromHttpErrorBlob(errorBlob, fallbackMessage)).pipe(
      switchMap(() =>
        throwError(
          () =>
            new Error('Unexpected PDF payload on failed download request.', {
              cause: err,
            }),
        ),
      ),
    );
  }

  const message = err instanceof Error ? err.message : fallbackMessage;
  return throwError(
    () => new Error(message || fallbackMessage, { cause: err }),
  );
}

export function mapPdfDownloadHttpResponse(
  response: HttpResponse<Blob>,
  fallbackMessage: string,
): Observable<Blob> {
  const blob = response.body;
  if (!blob || blob.size === 0) {
    return throwError(() => new Error(`${fallbackMessage}: empty file`));
  }
  return from(
    resolveDownloadedDocumentBlob(
      blob,
      response.headers.get('Content-Type'),
      fallbackMessage,
    ),
  );
}
